import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import {
  UploadFileOutlined as UploadIcon,
  CalendarTodayOutlined as WeekIcon,
  HistoryOutlined as HistoryIcon,
  TuneOutlined as RulesIcon,
} from '@mui/icons-material';
import { NavPanel, PanelGroup, PanelLink } from '../../components/layout/NavPanel';
import UploadStatementDialog from './UploadStatementDialog';
import type { RunDetail } from '../../api/services/payroll.service';

// Every payroll screen shares this, so the panel does not change under
// you when you move between the week, the history and somebody's rules.
//
// The one thing somebody comes here to do every Tuesday is at the top.
// The panel owns the dialog behind it, so the button is there on every
// screen in the section rather than on whichever one remembered to pass
// it in.
const PayrollPanel: React.FC<{
  onUploaded?: (detail: RunDetail) => void;
  // On /payroll/weeks/:id — whether that week is the newest. The newest is
  // "This week" however you reached it; any other belongs to History. The
  // exact-match link lit nothing on a week's own URL.
  latestWeek?: boolean;
  /** The current page's own panel content (Reports' filters), under the links. */
  children?: React.ReactNode;
}> = ({ onUploaded, latestWeek, children }) => {
  const { pathname } = useLocation();
  const onWeek = pathname.startsWith('/payroll/weeks/');
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <NavPanel>
      <Button
        fullWidth
        variant="contained"
        disableElevation
        startIcon={<UploadIcon />}
        onClick={() => setUploadOpen(true)}
        sx={{ mb: 2.5, py: 1 }}
      >
        Upload statement
      </Button>

      <PanelGroup label="Weeks">
        <PanelLink
          to="/payroll" icon={<WeekIcon />} label="This week" currentPath={pathname}
          active={pathname.replace(/\/+$/, '') === '/payroll' || (onWeek && latestWeek === true)}
        />
        <PanelLink
          to="/payroll/history" icon={<HistoryIcon />} label="History" currentPath={pathname}
          active={pathname.startsWith('/payroll/history') || (onWeek && latestWeek === false)}
        />
      </PanelGroup>

      <PanelGroup label="Setup">
        <PanelLink to="/payroll/people" icon={<RulesIcon />} label="Driver pay" currentPath={pathname} />
      </PanelGroup>

      {children}

      <UploadStatementDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(detail) => {
          setUploadOpen(false);
          onUploaded?.(detail);
          navigate(`/payroll/weeks/${detail.run._id}`);
        }}
      />
    </NavPanel>
  );
};

export default PayrollPanel;
